/**
 * Sticky Buy Bar — VHUES January Shipping Message Only
 * Full + Minimized Mode
 * No thresholds. No progress. No cart math.
 */

class StickyBuyBar {
  constructor(element) {
    this.bar = element;
    if (!this.bar) return;

    this.productId = this.bar.dataset.productId;
    this.triggerOffset = parseInt(this.bar.dataset.triggerOffset, 10) || 100;

    this.variantSelect   = this.bar.querySelector('[data-sticky-variant-select]');
    this.variantName     = this.bar.querySelector('[data-variant-name]');
    this.priceElement    = this.bar.querySelector('[data-sticky-price]');
    this.quantityInput   = this.bar.querySelector('[data-quantity-input]');
    this.addToCartBtn    = this.bar.querySelector('[data-sticky-add-to-cart]');
    this.closeBtn        = this.bar.querySelector('[data-sticky-close]');
    this.minimizedButton = document.querySelector('[data-sticky-minimized]');

    this.currentVariantId = this.addToCartBtn?.dataset.variantId;
    this.isMinimized = false;

    const minimizedSession =
      sessionStorage.getItem(`stickyBarMinimized_${this.productId}`) === 'true';

    this.init(minimizedSession);
  }

  init(minimizedSession) {
    if (this.minimizedButton) {
      this.minimizedButton.textContent = 'OPEN';
    }

    if (minimizedSession) {
      this.isMinimized = true;
      this.bar.classList.add('sticky-buy-bar--minimized');
      this.bar.classList.remove('is-visible');
      this.minimizedButton?.classList.add('is-active');
    } else {
      this.setupIntersectionObserver();
    }

    this.bindEvents();
  }

  setupIntersectionObserver() {
    const mainBuyButtons =
      document.querySelector('[name="add"]') ||
      document.querySelector('.product-form__submit') ||
      document.querySelector('[data-add-to-cart]') ||
      document.querySelector('button[type="submit"][name="add"]') ||
      document.querySelector('.shopify-payment-button');

    if (!mainBuyButtons) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (this.isMinimized) return;
          entry.isIntersecting ? this.hide() : this.show();
        });
      },
      {
        threshold: 0,
        rootMargin: `-${this.triggerOffset}px 0px 0px 0px`
      }
    );

    observer.observe(mainBuyButtons);
  }

  bindEvents() {
    this.variantSelect?.addEventListener('change', e =>
      this.handleVariantChange(e.target.value)
    );

    this.bar
      .querySelector('[data-quantity-minus]')
      ?.addEventListener('click', () => this.updateQuantity(-1));

    this.bar
      .querySelector('[data-quantity-plus]')
      ?.addEventListener('click', () => this.updateQuantity(1));

    this.quantityInput?.addEventListener('change', e => {
      const val = parseInt(e.target.value, 10);
      if (val < 1 || isNaN(val)) e.target.value = 1;
    });

    this.addToCartBtn?.addEventListener('click', () => this.addToCart());
    this.closeBtn?.addEventListener('click', () => this.minimize());
    this.minimizedButton?.addEventListener('click', () =>
      this.expandFromMinimized()
    );
  }

  handleVariantChange(variantId) {
    this.currentVariantId = variantId;
    if (this.addToCartBtn) {
      this.addToCartBtn.dataset.variantId = variantId;
    }

    const mainVariantSelect = document.querySelector(
      '.product-form select[name="id"]'
    );

    if (mainVariantSelect && mainVariantSelect.value !== variantId) {
      mainVariantSelect.value = variantId;
      mainVariantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateVariantDisplay(variantId);
  }

  updateVariantDisplay(variantId) {
    fetch(window.location.pathname + '.js')
      .then(res => res.json())
      .then(product => {
        const variant = product.variants.find(v => v.id == variantId);
        if (!variant) return;

        const priceSpan = this.priceElement?.querySelector('.price');
        const compareSpan = this.priceElement?.querySelector('.price--compare');

        if (priceSpan) priceSpan.textContent = this.formatMoney(variant.price);

        if (compareSpan) {
          if (variant.compare_at_price > variant.price) {
            compareSpan.textContent = this.formatMoney(variant.compare_at_price);
            compareSpan.style.display = 'inline';
          } else {
            compareSpan.style.display = 'none';
          }
        }

        if (this.variantName) {
          this.variantName.textContent = variant.title;
        }

        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = !variant.available;
          this.addToCartBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }
      });
  }

  updateQuantity(change) {
    const current = parseInt(this.quantityInput?.value, 10) || 1;
    const next = Math.max(1, current + change);
    if (this.quantityInput) this.quantityInput.value = next;
  }

  async addToCart() {
    if (!this.addToCartBtn) return;

    const qty = parseInt(this.quantityInput?.value, 10) || 1;
    const originalText = this.addToCartBtn.textContent;

    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Adding...';

    const themeATC = document.querySelector(
      '.product-form [name="add"], .product-form__submit, button[type="submit"][name="add"]'
    );

    if (themeATC) {
      try {
        const mainVariantInput = document.querySelector(
          '.product-form select[name="id"], .product-form input[name="id"]'
        );

        if (mainVariantInput && this.currentVariantId) {
          mainVariantInput.value = this.currentVariantId;
          mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const mainQtyInput = document.querySelector(
          '.product-form input[name="quantity"]'
        );
        if (mainQtyInput) {
          mainQtyInput.value = qty;
          mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        themeATC.click();

        this.addToCartBtn.textContent = '✓ Added!';
        this.addToCartBtn.classList.add('success');

        setTimeout(() => {
          this.addToCartBtn.textContent = originalText;
          this.addToCartBtn.classList.remove('success');
          this.addToCartBtn.disabled = false;
        }, 2000);
      } catch {
        this.addToCartBtn.textContent = originalText;
        this.addToCartBtn.disabled = false;
      }
    } else {
      this.addToCartBtn.textContent = originalText;
      this.addToCartBtn.disabled = false;
    }
  }

  show() {
    if (this.isMinimized) return;
    this.bar.style.display = 'block';
    this.bar.offsetHeight;
    requestAnimationFrame(() => this.bar.classList.add('is-visible'));
  }

  hide() {
    if (this.isMinimized) return;
    this.bar.classList.remove('is-visible');
  }

  minimize() {
    this.isMinimized = true;
    this.bar.classList.remove('is-visible');
    this.bar.classList.add('sticky-buy-bar--minimized');

    if (this.minimizedButton) {
      this.minimizedButton.classList.add('is-active');
      this.minimizedButton.textContent = 'OPEN';
    }

    sessionStorage.setItem(`stickyBarMinimized_${this.productId}`, 'true');
  }

  expandFromMinimized() {
    this.isMinimized = false;
    this.bar.classList.remove('sticky-buy-bar--minimized');
    sessionStorage.removeItem(`stickyBarMinimized_${this.productId}`);

    if (this.minimizedButton) {
      this.minimizedButton.classList.remove('is-active');
    }

    this.show();
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.sticky-buy-bar').forEach(bar => {
    new StickyBuyBar(bar);
  });
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const bar = e.target.querySelector('.sticky-buy-bar');
    if (bar) new StickyBuyBar(bar);
  });
}
